import { useState, useEffect } from 'react';
import { CatalogExam } from '../../types/catalog.response';
import { userExamsApi } from '../../services/api/user-exams.api';
import SearchableDropdown from '../../components/shared/SearchableDropdown';

interface ExamSettingsProps {
    catalogExams: CatalogExam[];
    catalogLoading: boolean;
}

interface ExistingExamRow {
    userItemId: number;
    catalogExamId: string;
    examName: string;
    examType: string;
    scoringType: string;
    score: string;
    scoreAlpha: string;
    originalScore: string;
    originalScoreAlpha: string;
}

interface NewExamRow {
    examType: string;
    examId: string;
    scoringType: string;
    score: string;
    scoreAlpha: string;
}

export default function ExamSettings({ catalogExams, catalogLoading }: ExamSettingsProps) {
    const [userExams, setUserExams] = useState<ExistingExamRow[]>([]);
    const [loadingExams, setLoadingExams] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
    const [rowSuccess, setRowSuccess] = useState<Record<number, boolean>>({});

    const [newExam, setNewExam] = useState<NewExamRow>({
        examType: '', examId: '', scoringType: '', score: '', scoreAlpha: ''
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        const fetchUserExams = async () => {
            try {
                const exams = await userExamsApi.getExams();
                setUserExams(exams.map(e => {
                    const score = e.userState.examScore?.toString() ?? '';
                    const scoreAlpha = e.userState.examScoreAlpha ?? '';
                    return {
                        userItemId: e.userItemId!,
                        catalogExamId: e.id,
                        examName: e.courseInfo.titleShort ?? e.id,
                        examType: e.courseInfo.subject,
                        scoringType: e.courseInfo.examScoringType ?? '',
                        score,
                        scoreAlpha,
                        originalScore: score,
                        originalScoreAlpha: scoreAlpha,
                    };
                }));
            } catch {
                setFetchError('Failed to load exams. Please refresh the page.');
            } finally {
                setLoadingExams(false);
            }
        };
        fetchUserExams();
    }, []);

    const handleScoreChange = (userItemId: number, value: string) => {
        setUserExams(prev => prev.map(e => e.userItemId === userItemId ? { ...e, score: value } : e));
        setRowErrors(prev => ({ ...prev, [userItemId]: '' }));
        setRowSuccess(prev => ({ ...prev, [userItemId]: false }));
    };

    const handleScoreAlphaChange = (userItemId: number, value: string) => {
        setUserExams(prev => prev.map(e => e.userItemId === userItemId ? { ...e, scoreAlpha: value } : e));
        setRowErrors(prev => ({ ...prev, [userItemId]: '' }));
        setRowSuccess(prev => ({ ...prev, [userItemId]: false }));
    };

    const handleSave = async (row: ExistingExamRow) => {
        setSavingId(row.userItemId);
        setRowErrors(prev => ({ ...prev, [row.userItemId]: '' }));
        try {
            await userExamsApi.updateExam(row.userItemId, {
                score: row.scoringType === 'NUMERIC' ? parseFloat(row.score) : null,
                scoreAlpha: row.scoringType === 'ALPHA' ? row.scoreAlpha : null,
            });
            setUserExams(prev => prev.map(e => e.userItemId === row.userItemId
                ? { ...e, originalScore: e.score, originalScoreAlpha: e.scoreAlpha }
                : e
            ));
            setRowSuccess(prev => ({ ...prev, [row.userItemId]: true }));
            setTimeout(() => setRowSuccess(prev => ({ ...prev, [row.userItemId]: false })), 2000);
        } catch {
            setRowErrors(prev => ({ ...prev, [row.userItemId]: 'Failed to save' }));
        } finally {
            setSavingId(null);
        }
    };

    const handleDelete = async (userItemId: number) => {
        setDeletingId(userItemId);
        setRowErrors(prev => ({ ...prev, [userItemId]: '' }));
        try {
            await userExamsApi.deleteExam(userItemId);
            setUserExams(prev => prev.filter(e => e.userItemId !== userItemId));
        } catch {
            setRowErrors(prev => ({ ...prev, [userItemId]: 'Failed to delete' }));
            setDeletingId(null);
        }
    };

    // Add new exam logic
    const examTypes = Array.from(new Set(catalogExams.map(e => e.type))).sort();
    const examsForType = catalogExams.filter(e => e.type === newExam.examType);
    const existingCatalogIds = userExams.map(e => e.catalogExamId);

    const handleNewTypeChange = (type: string) => {
        setNewExam({ examType: type, examId: '', scoringType: '', score: '', scoreAlpha: '' });
        setAddError(null);
    };

    const handleNewExamChange = (examId: string) => {
        const exam = catalogExams.find(e => e.id.toString() === examId);
        if (!exam) return;
        setNewExam(prev => ({ ...prev, examId, scoringType: exam.scoringType, score: '', scoreAlpha: '' }));
        setAddError(null);
    };

    const handleAdd = async () => {
        if (!newExam.examId) {
            setAddError('Please select an exam');
            return;
        }
        if (newExam.scoringType === 'NUMERIC' && !newExam.score) {
            setAddError('Please enter a score');
            return;
        }
        if (newExam.scoringType === 'ALPHA' && !newExam.scoreAlpha) {
            setAddError('Please enter a score');
            return;
        }

        setAdding(true);
        setAddError(null);
        try {
            const scoreToSend =
                newExam.scoringType === 'NUMERIC' ? parseFloat(newExam.score) :
                newExam.scoringType === 'PASS_FAIL' ? 1 :
                null;
            const scoreAlphaToSend = newExam.scoringType === 'ALPHA' ? newExam.scoreAlpha : null;

            const { data: added } = await userExamsApi.addExam(newExam.examId, scoreToSend, scoreAlphaToSend, null, []);
            const score = added.userState.examScore?.toString() ?? '';
            const scoreAlpha = added.userState.examScoreAlpha ?? '';
            setUserExams(prev => [...prev, {
                userItemId: added.userItemId!,
                catalogExamId: added.id,
                examName: added.courseInfo.titleShort ?? added.id,
                examType: added.courseInfo.subject,
                scoringType: added.courseInfo.examScoringType ?? '',
                score,
                scoreAlpha,
                originalScore: score,
                originalScoreAlpha: scoreAlpha,
            }]);
            setNewExam({ examType: '', examId: '', scoringType: '', score: '', scoreAlpha: '' });
            setShowAddForm(false);
        } catch {
            setAddError('Failed to add exam. Please try again.');
        } finally {
            setAdding(false);
        }
    };

    if (catalogLoading || loadingExams) {
        return <p className="text-sm text-gray-500">Loading exam data...</p>;
    }

    if (fetchError) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                <p className="text-sm">{fetchError}</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Existing exams */}
            <section>
                <h2 className="text-base font-medium text-gray-900 mb-1">Your Exams</h2>
                <p className="text-sm text-gray-500 mb-4">
                    Manage your exam scores. Changes are saved individually.
                </p>

                {userExams.length === 0 ? (
                    <p className="text-sm text-gray-400">No exams added yet.</p>
                ) : (
                    <div className="space-y-3">
                        {userExams.map(row => (
                            <div key={row.userItemId} className="rounded-xl border border-gray-200 bg-white p-4">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{row.examName}</p>
                                        <p className="text-xs text-gray-500">{row.examType}</p>
                                    </div>

                                    {row.scoringType === 'NUMERIC' && (
                                        <input
                                            type="number"
                                            step="any"
                                            value={row.score}
                                            onChange={e => handleScoreChange(row.userItemId, e.target.value)}
                                            placeholder="Score"
                                            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}

                                    {row.scoringType === 'ALPHA' && (
                                        <input
                                            type="text"
                                            value={row.scoreAlpha}
                                            onChange={e => handleScoreAlphaChange(row.userItemId, e.target.value)}
                                            placeholder="Score"
                                            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}

                                    {row.scoringType !== 'PASS_FAIL' && (
                                        <button
                                            type="button"
                                            onClick={() => handleSave(row)}
                                            disabled={
                                                savingId === row.userItemId ||
                                                (row.score === row.originalScore && row.scoreAlpha === row.originalScoreAlpha)
                                            }
                                            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {savingId === row.userItemId ? 'Saving...' : 'Save'}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(row.userItemId)}
                                        disabled={deletingId === row.userItemId}
                                        className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                                        aria-label="Remove exam"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>

                                {rowErrors[row.userItemId] && (
                                    <p className="mt-2 text-xs text-red-600">{rowErrors[row.userItemId]}</p>
                                )}
                                {rowSuccess[row.userItemId] && (
                                    <p className="mt-2 text-xs text-green-600">Saved successfully</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <hr className="border-gray-200" />

            {/* Add new exam */}
            <section>
                <h2 className="text-base font-medium text-gray-900 mb-1">Add Exam</h2>
                <p className="text-sm text-gray-500 mb-4">Add a new exam to your profile.</p>

                {!showAddForm ? (
                    <button
                        type="button"
                        onClick={() => setShowAddForm(true)}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Add exam
                    </button>
                ) : (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3 mb-28">
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <SearchableDropdown
                                    value={newExam.examType}
                                    options={examTypes.map(t => ({ _id: t, name: t }))}
                                    onChange={handleNewTypeChange}
                                    excludeIds={[]}
                                    placeholder="Select type..."
                                    noResultsText="No types found"
                                />
                            </div>

                            <div className="flex-1">
                                <SearchableDropdown
                                    value={newExam.examId}
                                    options={examsForType.map(e => ({ _id: e.id.toString(), name: e.name }))}
                                    onChange={handleNewExamChange}
                                    excludeIds={existingCatalogIds}
                                    placeholder={newExam.examType ? 'Select exam...' : 'Select type first...'}
                                    noResultsText="No exams found"
                                />
                            </div>

                            {newExam.scoringType === 'NUMERIC' && (
                                <input
                                    type="number"
                                    step="any"
                                    value={newExam.score}
                                    onChange={e => setNewExam(prev => ({ ...prev, score: e.target.value }))}
                                    placeholder="Score"
                                    className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            )}

                            {newExam.scoringType === 'ALPHA' && (
                                <input
                                    type="text"
                                    value={newExam.scoreAlpha}
                                    onChange={e => setNewExam(prev => ({ ...prev, scoreAlpha: e.target.value }))}
                                    placeholder="Score"
                                    className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            )}
                        </div>

                        {addError && (
                            <p className="text-xs text-red-600">{addError}</p>
                        )}

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleAdd}
                                disabled={adding}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {adding ? 'Adding...' : 'Add'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAddForm(false);
                                    setNewExam({ examType: '', examId: '', scoringType: '', score: '', scoreAlpha: '' });
                                    setAddError(null);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
